import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "../../constants";
import useCompanyStore from "../../hooks/useCompanyStore";

const MovieScreenCard = ({ screen }) => {
  const featureTimes = screen?.feature_times || [];
  const screenMovies = screen?.movies || [];
  const showDates = screen?.showDates || [];

  const formatShowDate = (dateString) => {
    const date = new Date(`${dateString}T00:00:00`);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const currentDate = new Date(date);
    currentDate.setHours(0, 0, 0, 0);

    let label = date.toLocaleDateString("en-US", {
      weekday: "short",
    });

    if (currentDate.getTime() === today.getTime()) {
      label = "Today";
    } else if (currentDate.getTime() === tomorrow.getTime()) {
      label = "Tomorrow";
    }

    return {
      label,
      date: date.toLocaleDateString("en-US", {
        day: "2-digit",
      }),
      month: date.toLocaleDateString("en-US", {
        month: "short",
      }),
      value: dateString,
    };
  };

  const dynamicShowDates = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return showDates
      .filter((dateString) => {
        const showDate = new Date(`${dateString}T00:00:00`);
        showDate.setHours(0, 0, 0, 0);

        return showDate >= today;
      })
      .slice(0, 3)
      .map(formatShowDate);
  }, [showDates]);

  const [selectedDate, setSelectedDate] = useState("");

  useEffect(() => {
    if (dynamicShowDates.length > 0) {
      setSelectedDate((prevDate) => {
        const stillAvailable = dynamicShowDates.some(
          (item) => item.value === prevDate,
        );

        return stillAvailable ? prevDate : dynamicShowDates[0].value;
      });
    } else {
      setSelectedDate("");
    }
  }, [dynamicShowDates]);

  const [selectedTime, setSelectedTime] = useState(
    featureTimes?.[0]?.start_time || screenMovies?.[0]?.showtime || "",
  );

  useEffect(() => {
    if (!selectedTime) {
      setSelectedTime(
        featureTimes?.[0]?.start_time || screenMovies?.[0]?.showtime || "",
      );
    }
  }, [featureTimes, screenMovies, selectedTime]);

  const selectedMovie = useMemo(() => {
    if (!screenMovies.length) {
      return null;
    }

    return (
      screenMovies.find(
        (movie) =>
          movie.showtime === selectedTime || movie.start_time === selectedTime,
      ) || null
    );
  }, [selectedTime, screenMovies]);

  const displayMovie = selectedMovie || screenMovies?.[0];

  if (!displayMovie) {
    return null;
  }

  const selectedFeatureTime =
    featureTimes.find((item) => item.start_time === selectedTime) || null;

  const detailsMovie = {
    id: displayMovie._id,
    showtimeId: displayMovie.showtimeId || displayMovie._id,
    title:
      displayMovie.nowShowing || displayMovie.movie_title || "Untitled Movie",
    image: displayMovie.poster_image || "../assets/images/no-image.jpg",
    rating: displayMovie.rating,
    genre: displayMovie.genre || "",
    suitability_label: displayMovie.suitability_label || "",
    language: displayMovie.language || "",
    duration: displayMovie.duration || "",
    description: displayMovie.description || "",
    adult_ticket_price: Number(displayMovie.adult_ticket_price || 0),
    child_ticket_price: Number(displayMovie.child_ticket_price || 0),
    screen: screen.screenName || "",
    screenId: screen.screenId || "",
    time:
      selectedTime || displayMovie.showtime || displayMovie.start_time || "",
    selectedDate,
    selectedFeatureTime,
    showDates: showDates,

    showtimes: featureTimes.map((time, index) => {
      const matchedMovie =
        screenMovies.find(
          (movie) =>
            movie.showtime === time.start_time ||
            movie.start_time === time.start_time,
        ) ||
        screenMovies[index] ||
        displayMovie;

      return {
        id: index + 1,
        time: time.start_time,
        start_time: time.start_time,
        label: time.label || time.feature_label || `Feature ${index + 1}`,
        sort_order: time.sort_order || index + 1,

        movieId: matchedMovie?._id,
        movie_title:
          matchedMovie?.nowShowing ||
          matchedMovie?.movie_title ||
          displayMovie.nowShowing ||
          displayMovie.movie_title ||
          "Untitled Movie",

        poster_image:
          matchedMovie?.poster_image ||
          displayMovie.poster_image ||
          "../assets/images/no-image.jpg",

        genre: matchedMovie?.genre || displayMovie.genre || "",
        suitability_label:
          matchedMovie?.suitability_label || displayMovie.suitability_label,

        description:
          matchedMovie?.description || displayMovie.description || "",

        duration: matchedMovie?.duration || displayMovie.duration || "",

        language: matchedMovie?.language || displayMovie.language || "",

        rating: matchedMovie?.rating || displayMovie.rating || "",

        adult_ticket_price: Number(matchedMovie?.adult_ticket_price || 0),
        child_ticket_price: Number(matchedMovie?.child_ticket_price || 0),
      };
    }),
  };

  return (
    <div className="movie-screen-card compact-screen-card">
      <div className="compact-screen-image">
        <img
          key={detailsMovie.image}
          src={detailsMovie.image}
          alt={detailsMovie.title}
          onError={(e) => {
            e.currentTarget.src = "../assets/images/no-image.jpg";
          }}
        />

        <div className="screen-badge compact-screen-badge">
          {screen.screenName}
        </div>
      </div>

      <div className="compact-screen-content">
        <div className="compact-screen-header">
          <h3>{detailsMovie.title}</h3>
        </div>

        <div className="compact-date-area">
          {dynamicShowDates.length > 0 ? (
            dynamicShowDates.map((item) => (
              <button
                type="button"
                key={item.value}
                className={`compact-date ${
                  selectedDate === item.value ? "active" : ""
                }`}
                onClick={() => setSelectedDate(item.value)}
              >
                <span>{item.label}</span>
                <strong>{item.date}</strong>
                <small>{item.month}</small>
              </button>
            ))
          ) : (
            <button type="button" className="compact-date active">
              <span>No Date</span>
              <strong>--</strong>
              <small>---</small>
            </button>
          )}
        </div>

        <div className="compact-timing-area">
          <div className="compact-timing-title">
            <i className="fa-solid fa-clock"></i>
            <span>Show Timings</span>
          </div>

          <div className="card-timing-buttons compact-time-buttons">
            {featureTimes.length > 0 ? (
              featureTimes.map((time, index) => (
                <button
                  type="button"
                  key={`${time.start_time}-${index}`}
                  className={selectedTime === time.start_time ? "active" : ""}
                  onClick={() => setSelectedTime(time.start_time)}
                >
                  {time.start_time}
                </button>
              ))
            ) : (
              <button type="button" className="active">
                {detailsMovie.time || "Time N/A"}
              </button>
            )}
          </div>
        </div>

        <Link
          to={`/screens/${detailsMovie.id}`}
          state={{
            movie: detailsMovie,
            screen,
            selectedTime,
            selectedDate,
          }}
          className="view-details-btn compact-view-btn"
        >
          View Details
        </Link>
      </div>
    </div>
  );
};

const MovieScreens = () => {
  const [movieScreen, setMovieScreen] = useState([]);
  const { companyId } = useCompanyStore();
  const fetchMovieScreen = async () => {
    try {
      const response = await fetch(API.LIST_MOVIE_SCREEN + "/" + companyId, {
        method: "GET",
      });

      const result = await response.json();

      if (!response.ok || result.status === false) {
        setMovieScreen([]);
        return;
      }

      setMovieScreen(result.data || []);
    } catch (error) {

      setMovieScreen([]);
    }
  };

  useEffect(() => {
    if (!companyId) return;
    fetchMovieScreen();
  }, [companyId]);

  return (
    <section className="movie-section">
      <div className="container">
        <div className="section-heading">
          <span>Choose Your Experience</span>

          <h2>Movie Screens</h2>

          <p>Explore our cinema screens and enjoy the best movie experience.</p>
        </div>

        <div className="row g-4">
          {movieScreen.length > 0 ? (
            movieScreen.map((screen, index) => (
              <div
                className="col-md-6 col-sm-12"
                key={screen.screenId || index}
              >
                <MovieScreenCard screen={screen} />
              </div>
            ))
          ) : (
            <div className="col-12 text-center">
              <p>No movie screens found.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default MovieScreens;
